import { isSafe as isSafeRegex } from "redos-detector";

/** Problem:
 * Agentic access requires the GCP WIF integration to function, and GCP WIF
 * requires an IAM security perimeter in turn. The perimeter admits a member
 * only if it matches `DOMAIN_ALLOW_PATTERN`, a regex P0's setup steps generate
 * to match the tenant's email domain -- human identities are identified by
 * email. Agentic grants are made to a WIF principal URI instead, which no
 * email-shaped pattern matches, so the perimeter vetoes every agentic grant
 * until it is reconfigured.
 *
 * This module answers only "would that happen, and what should they use
 * instead?", so the WIF installer can warn before the problem is hit. It is a
 * bandaid: the durable fix is to coordinate the two integrations properly.
 */

/** A WIF principal member, standing in for any agentic grant.
 *
 * The pool this install will create has no ids yet, and none of the patterns
 * involved here distinguish one pool from another, so concrete ids would add
 * nothing. Must stay in step with `wifMember` in the backend's
 * `integrations/resources/gcloud/wif.ts`, which builds what is really sent.
 */
const EXAMPLE_WIF_MEMBER =
  "principal://iam.googleapis.com/projects/000000000000/locations/global/workloadIdentityPools/p0-wif-pool-example/subject/example-subject";

/** A regex branch matching any P0-issued WIF principal member. */
export const WIF_MEMBER_ALLOW_PATTERN =
  "^//iam\\.googleapis\\.com/projects/[0-9]+/locations/global/workloadIdentityPools/[^/]+/subject/.+$";

/** Compiles `source`, unless it is invalid or could backtrack catastrophically.
 *
 * The pattern comes from the customer's perimeter, so nothing constrains its
 * shape; running it unchecked would let it hang the page it renders on.
 * Compiling is safe on its own -- `isSafeRegex` decides whether running it is.
 */
const safePattern = (source: string): RegExp | undefined => {
  try {
    // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
    const compiled = new RegExp(source);
    return isSafeRegex(compiled).safe ? compiled : undefined;
  } catch {
    return undefined;
  }
};

/** Whether the perimeter would reject agentic grants under this pattern.
 *
 * Mirrors the perimeter's own test (`member-verify.ts` in
 * p0-security-perimeter): it splits the member on `:`, drops the first
 * segment, and regex-tests `Array.prototype.join()` of the rest -- which
 * rejoins with `,`, not `:`, so a subject containing colons (e.g.
 * Kubernetes's `system:serviceaccount:ns:agent`) is tested as
 * `system,serviceaccount,ns,agent`. That must be matched exactly, since a
 * customer's pattern is tested against the same string the perimeter itself
 * builds. Treats an unset pattern as admitting everyone; a pattern it cannot
 * use is reported as blocking -- an invalid one stops the perimeter from
 * starting at all.
 */
export const blocksAgenticGrants = (
  allowedDomains: string | undefined
): boolean => {
  if (!allowedDomains) return false;
  const pattern = safePattern(allowedDomains);
  if (!pattern) return true;
  return !pattern.test(EXAMPLE_WIF_MEMBER.split(":").slice(1).join());
};

/** Widens an existing pattern so it also admits WIF principals, or `undefined`
 * if the existing pattern cannot be built on.
 *
 * The existing pattern is kept verbatim in a non-capturing group rather than
 * rewritten, so its restriction keeps exactly today's semantics and the only
 * members newly admitted are WIF principals. A pattern that is invalid or
 * unsafe yields no suggestion: composing it would carry that straight into
 * what we recommend, and dropping it instead would widen the perimeter to
 * admit everyone.
 */
export const suggestedDomainAllowPattern = (
  allowedDomains: string | undefined
): string | undefined => {
  if (!allowedDomains) return WIF_MEMBER_ALLOW_PATTERN;
  if (!safePattern(allowedDomains)) return undefined;
  return `(?:${allowedDomains})|(?:${WIF_MEMBER_ALLOW_PATTERN})`;
};
